import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import AllChatsBackupList from './all-chats-backup-list';
import type { BackupSource } from './use-backup-source';

/**
 * /view-backup — an admin-only verification page. Two tabs, each a pure
 * single-tier all-chats message list, so you can confirm messages really landed
 * in the local vault and in the server DB backup:
 *   • Vault — read through the WhatsApp Web engine (the vault is IndexedDB on
 *     the web.whatsapp.com origin, unreachable directly from this origin).
 *   • DB    — the owner-scoped `wa-messages-backup` collection.
 * Scroll to the top of a tab to page in older messages across every chat.
 */
const ViewBackupView: React.FC = () => {
    const [tab, setTab] = useState<BackupSource>('vault');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'var(--whatsapp-chat-bg, #efeae2)' }}>
            <Box sx={{ px: 2, pt: 2, pb: 0, bgcolor: 'var(--background-default, #fff)', borderBottom: '1px solid var(--divider-color, rgba(0,0,0,0.08))' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-text-color, #111b21)' }}>
                    Backup Viewer
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--secondary-text-color, #667781)', mb: 1 }}>
                    Verify captured messages across all chats. Scroll up to load older.
                </Typography>
                <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 40 }}>
                    <Tab
                        value="vault"
                        icon={<Inventory2RoundedIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Vault (local)"
                        sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        value="db"
                        icon={<StorageRoundedIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="DB (server)"
                        sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                    />
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                {/* Keyed remount so switching tiers resets paging cleanly. */}
                <AllChatsBackupList key={tab} source={tab} />
            </Box>
        </Box>
    );
};

export default ViewBackupView;
